import { jsx as _jsx } from "react/jsx-runtime";
import { afterAnimationFrame } from '@prophecy/utils/dom';
import { KeyCodes } from '@prophecy/utils/keyboard';
import { useOnMount, usePersistentCallback } from '@prophecy/utils/react/hooks';
import { PlateElement, useElement } from '@udecode/plate/react';
import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { SELECT_DROPDOWN_CLASS, SELECT_SELECTION_SEARCH_CLASS } from '../Select/tokens';
import { StyledMentionAutoComplete } from './styled';
import { useActiveOption } from './useActiveOption';
function TriggerInputElementInner({ trigger, getSuggestions = ()=>Promise.resolve([]), renderSuggestion, onSelect: onSelectProp, onTrigger, onActiveOptionChange, endCharRegex, editorContainerRef, dropdownMatchSelectWidth = 300, dataTestId = 'trigger-autocomplete', ...plateProps }, ref) {
    const { editor } = plateProps;
    const [search, setSearch] = useState(trigger);
    const autoCompleteWrapperRef = useRef(null);
    const isEndCharRef = useRef(false);
    const element = useElement();
    const removed = useRef(false);
    const [suggestions, setSuggestions] = useState([]);
    const persistentGetSuggestions = usePersistentCallback(getSuggestions);
    useEffect(()=>{
        const searchText = search.startsWith(trigger) ? search.slice(trigger.length) : search;
        persistentGetSuggestions(searchText).then(setSuggestions);
    }, [
        persistentGetSuggestions,
        search,
        trigger
    ]);
    const removeElement = ()=>{
        const path = editor.api.findPath(element);
        if (!path) return;
        editor.tf.removeNodes({
            at: path
        });
        editorContainerRef.current?.focus();
    };
    // we use data-value on input wrapper to control the input width
    // We need to manually update the attribute data-value to avoid a react cycle causing flicker.
    const updateParentAttribute = (value)=>{
        autoCompleteWrapperRef.current?.querySelector(`.${SELECT_SELECTION_SEARCH_CLASS}`)?.setAttribute('data-value', value);
    };
    useOnMount(()=>{
        afterAnimationFrame(()=>{
            autoCompleteWrapperRef.current?.querySelector('input')?.focus();
        });
        updateParentAttribute(search);
        onTrigger?.();
    });
    const options = useMemo(()=>suggestions.map((suggestion)=>({
                ...suggestion,
                value: `${trigger}${suggestion.value}`,
                key: suggestion.value
            })), [
        suggestions,
        trigger
    ]);
    // Updating options during search is causing blur issues
    // calling handleSelect erroneously
    const mentionOptionsRemountKey = useMemo(()=>[
            ...options
        ].map((o)=>String(o.key ?? o.value)).sort((a, b)=>a.localeCompare(b))// ASCII Unit Separator (U+001F): Safer over other delimiters
        .join('\u001f'), [
        options
    ]);
    useEffect(()=>{
        removed.current = false;
        return ()=>{
            removed.current = true;
        };
    }, [
        mentionOptionsRemountKey
    ]);
    const cleanValue = (option)=>({
            ...option,
            value: option.value.startsWith(trigger) ? option.value.slice(1) : option.value
        });
    // Tracks real user interaction (pointer/keyboard/typing) so spurious blur can refocus.
    // useActiveOption's first truthy callback mirrors defaultActiveFirstOption (not user-driven).
    const isInteractingRef = useRef(false);
    const skipInteractionPinOnNextTruthyActiveOptionRef = useRef(true);
    useEffect(()=>{
        skipInteractionPinOnNextTruthyActiveOptionRef.current = true;
    }, [
        options
    ]);
    const markUserInteractingWithMentionUI = usePersistentCallback(()=>{
        isInteractingRef.current = true;
    });
    const wrappedOnActiveOptionChange = usePersistentCallback((option)=>{
        if (option) {
            if (skipInteractionPinOnNextTruthyActiveOptionRef.current) {
                skipInteractionPinOnNextTruthyActiveOptionRef.current = false;
            } else {
                isInteractingRef.current = true;
            }
        }
        onActiveOptionChange?.(option);
    });
    useActiveOption(autoCompleteWrapperRef, options, cleanValue, wrappedOnActiveOptionChange);
    const handleSelect = (option)=>{
        if (removed.current) return;
        removed.current = true;
        const currentSearch = search;
        updateParentAttribute('');
        setSearch('');
        removeElement();
        onActiveOptionChange?.(undefined);
        onSelectProp(option ? cleanValue(option) : undefined, currentSearch);
    };
    // rc-select and canvas interactions can cause spurious blur events.
    // Only commit when focus has truly left the mention UI.
    const handleBlur = usePersistentCallback((e)=>{
        if (removed.current) return;
        if (isEndCharRef.current) {
            isEndCharRef.current = false;
            handleSelect();
            return;
        }
        const related = e.relatedTarget;
        const wrapper = autoCompleteWrapperRef.current;
        if (related?.closest?.(`.${SELECT_DROPDOWN_CLASS}`)) return;
        if (related && wrapper?.contains(related)) return;
        requestAnimationFrame(()=>{
            if (removed.current) return;
            if (isInteractingRef.current) {
                isInteractingRef.current = false;
                autoCompleteWrapperRef.current?.querySelector('input')?.focus();
                return;
            }
            const active = document.activeElement;
            if (active?.closest?.(`.${SELECT_DROPDOWN_CLASS}`)) return;
            if (wrapper?.contains(active)) return;
            handleSelect();
        });
    });
    const handleDropdownVisibleChange = (visible)=>{
        if (!visible) {
            onActiveOptionChange?.(undefined);
        }
    };
    return /*#__PURE__*/ _jsx(PlateElement, {
        ref: ref,
        as: "span",
        ...plateProps,
        children: /*#__PURE__*/ _jsx("span", {
            contentEditable: false,
            ref: autoCompleteWrapperRef,
            onPointerDownCapture: markUserInteractingWithMentionUI,
            onKeyDownCapture: markUserInteractingWithMentionUI,
            children: /*#__PURE__*/ _jsx(StyledMentionAutoComplete, {
                options: options,
                defaultOpen: true,
                defaultActiveFirstOption: true,
                value: search,
                searchValue: search,
                showSearch: false,
                filterOption: false,
                onBlur: handleBlur,
                onSearch: updateParentAttribute,
                optionRender: renderSuggestion ? (option)=>renderSuggestion(cleanValue(option.data)) : undefined,
                onChange: (value)=>{
                    markUserInteractingWithMentionUI();
                    setSearch(value);
                    updateParentAttribute(value);
                },
                onKeyDown: (e)=>{
                    const { key } = e;
                    e.stopPropagation();
                    if (key === 'Enter') {
                        const item = options.find((o)=>o.value.toLowerCase().includes(search.toLowerCase()));
                        if (item) {
                            handleSelect(item);
                        } else {
                            if (removed.current) return;
                            removed.current = true;
                            onActiveOptionChange?.(undefined);
                            const currentSearch = search;
                            updateParentAttribute('');
                            setSearch('');
                            // remove the special node and make it plain text
                            const path = editor.api.findPath(element);
                            if (path) {
                                editor.tf.removeNodes({
                                    at: path
                                });
                                editor.tf.insertText(currentSearch);
                            }
                            // trigger a normal enter event on the editor container so the prompt gets sent
                            afterAnimationFrame(()=>{
                                editorContainerRef.current?.focus();
                                editorContainerRef.current?.dispatchEvent(new KeyboardEvent('keydown', {
                                    key: KeyCodes.Enter,
                                    code: KeyCodes.Enter,
                                    bubbles: true,
                                    cancelable: true,
                                    shiftKey: e.shiftKey
                                }));
                            });
                        }
                    } else if (endCharRegex?.test(key)) {
                        isEndCharRef.current = true;
                        // trigger manual blur so the input becomes a mention
                        autoCompleteWrapperRef.current?.querySelector('input')?.blur();
                    } else if (key === 'Escape') {
                        e.preventDefault();
                        removed.current = true;
                        onActiveOptionChange?.(undefined);
                        removeElement();
                    } else if (key === 'Backspace' && search === trigger) {
                        e.preventDefault();
                        removed.current = true;
                        onActiveOptionChange?.(undefined);
                        removeElement();
                    }
                //TODO: handle arrow left and right while the user is inside the mention input
                },
                onSelect: (_, option)=>{
                    handleSelect(option);
                },
                dropdownMatchSelectWidth: dropdownMatchSelectWidth,
                disableScrollClose: true,
                onDropdownVisibleChange: handleDropdownVisibleChange,
                "data-test-id": dataTestId
            }, mentionOptionsRemountKey)
        })
    });
}
export const TriggerInputElement = /*#__PURE__*/ forwardRef(TriggerInputElementInner);
